import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  HowItWorks,
  StatusRow,
  MetricCard,
  CheckItem,
  FakeSelect,
  ProgressBar,
  Tag,
} from "@/components/HowItWorks";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Bot,
  ArrowRight,
  ArrowUpRight,
  Zap,
  MessageSquare,
  BookOpen,
  Settings,
  Sparkles,
  Check,
  Search,
  Blocks,
  Users,
  CalendarDays,
  Clock,
  Globe,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveAssetUrl } from "@/lib/media";

import openaiLogo from "@/assets/partners/openai.svg";
import anthropicLogo from "@/assets/partners/anthropic.svg";
import langchainLogo from "@/assets/partners/langchain.svg";
import makeLogo from "@/assets/partners/make.svg";
import zapierLogo from "@/assets/partners/zapier.svg";
import supabaseLogo from "@/assets/partners/supabase.svg";
import hubspotLogo from "@/assets/partners/hubspot.svg";
import slackLogo from "@/assets/partners/slack.svg";

const highlights = [
  {
    icon: Bot,
    title: "AI Agents & Automation",
    description:
      "Autonomous AI agents that handle support, order processing, and repetitive workflows at scale.",
  },
  {
    icon: MessageSquare,
    title: "Chatbots & Assistants",
    description:
      "Intelligent chatbots that understand your business and deliver 24/7 support across every channel.",
  },
  {
    icon: Settings,
    title: "Custom AI Tools",
    description:
      "Tailored AI dashboards, recommendation engines, and predictive analytics built around your needs.",
  },
];

const processSteps = [
  {
    title: "Discover & Define Use Case",
    description:
      "We analyse your business, identify high-impact AI opportunities, and define clear goals and success metrics.",
    preview: (
      <div className="space-y-3">
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
          <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5"><Search className="h-3 w-3" />AI Opportunities</p>
          <div className="space-y-2.5">
            <CheckItem label="Customer support automation" checked delay={0.05} />
            <CheckItem label="Content generation pipeline" checked delay={0.1} />
            <CheckItem label="Internal knowledge base" delay={0.15} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FakeSelect label="Priority" value="High Impact" icon={Zap} delay={0.2} />
          <FakeSelect label="Timeline" value={"4\u20136 weeks"} icon={Clock} delay={0.25} />
        </div>
      </div>
    ),
  },
  {
    title: "Prototype & Validate",
    description:
      "We build rapid prototypes to test assumptions and validate the solution before committing to full development.",
    preview: (
      <div className="space-y-3">
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
          <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" />Chat Preview</p>
          <div className="space-y-2">
            <div className="flex gap-2 items-start">
              <div className="w-6 h-6 rounded-full bg-secondary shrink-0" />
              <div className="bg-secondary rounded-lg rounded-tl-none px-3 py-2 text-xs text-foreground max-w-[80%]">
                How can I track my order?
              </div>
            </div>
            <div className="flex gap-2 items-start justify-end">
              <div className="bg-primary/10 rounded-lg rounded-tr-none px-3 py-2 text-xs text-foreground max-w-[80%]">
                I can help! Your order #4821 shipped yesterday and arrives Thursday.
              </div>
              <div className="w-6 h-6 rounded-full bg-primary/20 shrink-0 flex items-center justify-center">
                <Bot className="h-3 w-3 text-primary" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <MetricCard label="Accuracy" value="94%" trend="up" icon={Check} delay={0.15} />
          <MetricCard label="Avg Response" value="1.2s" trend="down" icon={Clock} delay={0.2} />
        </div>
      </div>
    ),
  },
  {
    title: "Build & Integrate",
    description:
      "We develop the AI solution and integrate it into your existing systems, CRMs, and workflows.",
    preview: (
      <div className="space-y-2">
        <StatusRow label="OpenAI GPT-4" status="Active" connected logo={openaiLogo} delay={0.05} />
        <StatusRow label="Supabase" status="Connected" connected logo={supabaseLogo} delay={0.1} />
        <StatusRow label="Slack" status="Connected" connected logo={slackLogo} delay={0.15} />
        <StatusRow label="HubSpot CRM" status="Syncing" logo={hubspotLogo} delay={0.2} />
        <ProgressBar label="Deployment" percent={78} delay={0.25} icon={Zap} />
      </div>
    ),
  },
  {
    title: "Optimise & Scale",
    description:
      "We monitor performance, refine models, and scale the solution as your business grows and needs evolve.",
    preview: (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <MetricCard label="Queries/day" value="2.4k" trend="up" icon={Activity} delay={0.05} />
          <MetricCard label="Resolution" value="89%" trend="up" icon={Check} delay={0.1} />
          <MetricCard label="Cost/query" value="$0.02" trend="down" icon={Zap} delay={0.15} />
        </div>
        <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)] space-y-3">
          <ProgressBar label="Model Accuracy" percent={94} color="bg-emerald-500" delay={0.2} icon={Bot} />
          <ProgressBar label="User Satisfaction" percent={91} delay={0.25} icon={Users} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Tag active delay={0.3} icon={Globe}>English</Tag>
          <Tag active delay={0.33} icon={Globe}>Arabic</Tag>
          <Tag delay={0.36} icon={Globe}>French</Tag>
          <Tag delay={0.39} icon={MessageSquare}>WhatsApp</Tag>
          <Tag delay={0.42} icon={MessageSquare}>Web Chat</Tag>
        </div>
      </div>
    ),
  },
];

const services = [
  {
    icon: Bot,
    title: "AI Agents & Automation",
    description:
      "Autonomous AI agents that handle support, order processing, and repetitive workflows at scale.",
    slug: "ai-agents-automation",
  },
  {
    icon: Sparkles,
    title: "AI-Powered Content Generation",
    description:
      "Generate product descriptions, blog posts, and ad copy optimised for SEO and conversions.",
    slug: "ai-content-generation",
  },
  {
    icon: MessageSquare,
    title: "AI Chatbots & Assistants",
    description:
      "Intelligent chatbots that understand your business and deliver 24/7 support across every channel.",
    slug: "ai-chatbots-assistants",
  },
  {
    icon: Zap,
    title: "AI Integrations for Workflows",
    description:
      "Integrate AI into your CRMs, ERPs, and internal tools to automate processes and boost efficiency.",
    slug: "ai-integrations",
  },
  {
    icon: BookOpen,
    title: "Knowledge Base Automation",
    description:
      "Turn your documentation into a smart AI knowledge base that delivers instant answers on demand.",
    slug: "knowledge-base-automation",
  },
  {
    icon: Settings,
    title: "Custom AI Tools",
    description:
      "Tailored AI dashboards, recommendation engines, and predictive analytics built around your needs.",
    slug: "custom-ai-tools",
  },
];

const whyReasons = [
  "Real business use-cases, not experimental AI",
  "Fast implementation using modern AI stack",
  "Seamless integration with your existing systems",
  "Scalable solutions for GCC businesses",
  "Arabic + English AI capabilities",
];

const techStack = [
  {
    name: "OpenAI & Anthropic",
    logos: [openaiLogo, anthropicLogo],
    description:
      "State-of-the-art language models for intelligent conversation, content generation, and reasoning.",
  },
  {
    name: "LangChain & LangGraph",
    logos: [langchainLogo],
    description:
      "Framework for building complex AI agents with memory, tool use, and multi-step reasoning.",
  },
  {
    name: "Make & Zapier",
    logos: [makeLogo, zapierLogo],
    description:
      "No-code automation platforms to connect AI with your existing apps and workflows.",
  },
  {
    name: "Supabase & Vector DBs",
    logos: [supabaseLogo],
    description:
      "Database and vector storage for AI knowledge bases, embeddings, and real-time data.",
  },
];

const faqs = [
  {
    q: "What kind of AI solutions do you build?",
    a: "We build practical AI solutions including chatbots, autonomous agents, content generators, knowledge bases, and custom AI tools. Everything is designed for real business impact, not experimental tech.",
  },
  {
    q: "How long does an AI project typically take?",
    a: "A simple chatbot or automation can be delivered in 2\u20134 weeks. More complex AI agents or custom tools take 6\u201312 weeks depending on integrations, data requirements, and training needs.",
  },
  {
    q: "Do your AI solutions support Arabic?",
    a: "Yes. We build bilingual AI solutions that understand and respond in both Arabic and English, with cultural context awareness for the GCC market.",
  },
  {
    q: "How do you integrate AI with existing systems?",
    a: "We connect AI solutions to your CRMs (HubSpot, Salesforce), ERPs, ecommerce platforms (Shopify), communication tools (Slack, WhatsApp), and custom APIs via secure integrations.",
  },
  {
    q: "What\u2019s the ROI of AI automation?",
    a: "Our clients typically see 40\u201380% reduction in manual tasks, 24/7 customer support coverage, and measurable improvements in response times, conversion rates, and operational efficiency.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const AIPage = () => {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/ai");
  useCmsPageSEO({
    title:
      lang === "ar"
        ? "وكالة تطوير الذكاء الاصطناعي في الكويت | وافاي"
        : "AI Development Agency in Kuwait | Wavai",
    description:
      lang === "ar"
        ? "تبني وافاي وكلاء وأتمتة وتطبيقات ذكية للأعمال في الكويت ودول الخليج. ذكاء اصطناعي عملي يخفض التكلفة ويوفر الوقت ويزيد الإيرادات."
        : "Wavai builds AI agents, automations and intelligent apps for Kuwait and GCC businesses. Practical AI that cuts cost, saves time and drives revenue.",
    keywords:
      "AI agency Kuwait, AI automation Kuwait, chatbots Kuwait, AI agents GCC, custom AI tools Kuwait",
    canonical,
    hreflang,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI Solutions & Automation",
      provider: {
        "@type": "Organization",
        name: "Wavai",
        url: "https://www.wavai.ae",
      },
      description:
        "AI-powered automation, chatbots, content generation, and custom AI tools for businesses.",
    },
  });

  const { data: works } = useQuery({
    queryKey: ["ai-work"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work")
        .select(
          "title, slug, short_description, featured_image, desktop_screenshot, industry, platforms"
        )
        .eq("status", "published")
        .contains("platforms", ["Lovable"])
        .order("sort_order", { ascending: true })
        .limit(4);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* ------------------------------------------------------------------ */}
        {/* HERO                                                               */}
        {/* ------------------------------------------------------------------ */}
        <section className="pt-36 pb-16 lg:pt-48 lg:pb-24">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={0}
              >
                {t("AI Services")}
              </motion.span>

              <motion.h1
                className="text-4xl sm:text-5xl lg:text-[3.5rem] font-display font-bold leading-tight mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={1}
              >
                {t("AI Development Agency in Kuwait")}
              </motion.h1>

              <motion.p
                className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Wavai builds practical AI — agents, automations and intelligent apps — that help Kuwait businesses cut cost, save time and serve customers around the clock. No hype. Just real outcomes."
                )}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4 [&>*]:w-full [&>*]:sm:w-auto"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={3}
              >
                <Button variant="hero" size="xl" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project")} <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="xl" asChild>
                  <Link to={localePath("/contact")}>{t("Contact Us")}</Link>
                </Button>
              </motion.div>
            </div>

            {/* 3 Highlight cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {highlights.map((h, i) => (
                <motion.div
                  key={h.title}
                  className="bg-secondary/50 rounded-2xl p-8 flex flex-col gap-8"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                >
                  <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center shadow-card border border-border">
                    <h.icon
                      className="h-6 w-6 text-foreground"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-lg mb-2">
                      {t(h.title)}
                    </h3>
                    <p className="text-base text-muted-foreground leading-relaxed text-balance">
                      {t(h.description)}
                    </p>
                  </div>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-base text-muted-foreground font-medium">
                    {t("Learn more")}
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* HOW IT WORKS                                                       */}
        {/* ------------------------------------------------------------------ */}
        <HowItWorks
          steps={processSteps}
          heading={t("From idea to intelligent solution")}
          subtitle={t(
            "A structured approach to delivering AI solutions that work, from discovery to scale."
          )}
          trustText={t("Trusted by businesses automating with AI across the GCC.")}
          t={t}
        />

        {/* ------------------------------------------------------------------ */}
        {/* SERVICES                                                           */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24 bg-secondary/30">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-background text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("What We Offer")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("AI Services")}
              </motion.h2>
              <motion.p
                className="text-base text-muted-foreground leading-relaxed"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={2}
              >
                {t(
                  "Practical AI solutions designed for real business impact, not buzzwords."
                )}
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-border">
              {services.map((service, i) => (
                <motion.div
                  key={service.slug}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="border-b border-border"
                >
                  <Link
                    to={localePath(`/ai/${service.slug}`)}
                    className="group block p-8 h-full rounded-none hover:rounded-2xl hover:bg-card hover:shadow-[0_8px_30px_-12px_hsl(var(--foreground)/0.12)] transition-[background-color,border-radius,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-6 transition-colors duration-200 group-hover:bg-primary/15">
                      <service.icon
                        className="h-5 w-5 text-primary"
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="font-sans font-medium text-lg mb-2 group-hover:text-primary transition-colors duration-200">
                      {t(service.title)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(service.description)}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* WHY WAVAI + TECH STACK                                             */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Left - Why Wavai */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4">
                  {t("Why Wavai")}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4">
                  {t("Why choose Wavai for AI")}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-8">
                  {t(
                    "We build AI solutions that solve real problems: fast, reliable, and built to scale with your business."
                  )}
                </p>
                <div className="space-y-4">
                  {whyReasons.map((reason, i) => (
                    <motion.div
                      key={reason}
                      className="flex items-center gap-3"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeUp}
                      custom={i}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Check className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <span className="text-base text-foreground">
                        {t(reason)}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right - Tech stack */}
              <div className="flex flex-col gap-6">
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium w-fit mb-2">
                  {t("Tech Stack")}
                </span>
                {techStack.map((item, i) => (
                  <motion.div
                    key={item.name}
                    className="flex gap-5 items-start"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                  >
                    <div className="w-16 h-16 rounded-2xl border border-border bg-card flex items-center justify-center shrink-0 shadow-card gap-1.5">
                      {item.logos.map((logo, j) => (
                        <img key={j} src={logo} alt="" className="h-6 w-6 object-contain" />
                      ))}
                    </div>
                    <div>
                      <h3 className="font-sans font-medium text-lg mb-1">
                        {item.name}
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed">
                        {t(item.description)}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* WORK SHOWCASE                                                      */}
        {/* ------------------------------------------------------------------ */}
        {works && works.length > 0 && (
          <section className="py-16 lg:py-32 bg-foreground text-background">
            <div className="container px-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={0}
                >
                  <p className="text-sm uppercase tracking-[0.25em] text-primary font-medium mb-4">
                    {t("Portfolio")}
                  </p>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-background">
                    {t("Selected")}{" "}
                    <span className="text-gradient">{t("Work")}</span>
                  </h2>
                </motion.div>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={1}
                >
                  <Link
                    to={localePath("/work")}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  >
                    {t("View all projects")} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
                {works.map((work, i) => {
                  const img = resolveAssetUrl(
                    work.desktop_screenshot || work.featured_image
                  );
                  return (
                    <motion.div
                      key={work.slug}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-60px" }}
                      variants={fadeUp}
                      custom={i}
                    >
                      <Link
                        to={localePath(`/work/${work.slug}`)}
                        className="group block"
                      >
                        {img && (
                          <div className="aspect-[16/10] overflow-hidden rounded-xl bg-background/5 mb-5">
                            <img
                              src={img}
                              alt={work.title}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-sans font-semibold text-base lg:text-[20px] text-background group-hover:text-primary transition-colors duration-200">
                              {work.title}
                            </h3>
                            {work.industry && (
                              <p className="text-sm text-background/50 mt-1">
                                {work.industry}
                              </p>
                            )}
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-background/30 group-hover:text-primary flex-shrink-0 mt-1 transition-colors duration-200" />
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* FAQ                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("FAQ")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Frequently asked questions")}
              </motion.h2>
            </div>

            <div className="max-w-[700px] mx-auto">
              <Accordion type="single" collapsible>
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base">
                      {t(faq.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {t(faq.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* CTA                                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-16 lg:py-24">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                {t("Get Started")}
              </motion.span>
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                {t("Ready to unlock AI for your business?")}
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <motion.div
                className="bg-secondary/50 rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={0}
              >
                <Users className="h-8 w-8 text-foreground" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2">
                    {t("Start a project")}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    {t(
                      "Tell us about your AI goals and get a tailored proposal within 24 hours."
                    )}
                  </p>
                </div>
                <Button variant="hero" size="lg" className="w-fit" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Get a Free Quote")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                className="bg-primary rounded-2xl p-10 lg:p-12 flex flex-col gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={1}
              >
                <CalendarDays
                  className="h-8 w-8 text-white"
                  strokeWidth={1.5}
                />
                <div>
                  <h3 className="font-display text-2xl font-bold mb-2 text-white">
                    {t("Book a free consultation")}
                  </h3>
                  <p className="text-base text-white/70 leading-relaxed">
                    {t(
                      "Chat with an AI expert to explore how automation can transform your operations."
                    )}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="w-fit border-2 border-white/30 bg-transparent text-white hover:bg-white/10"
                  asChild
                >
                  <Link to={localePath("/contact")}>
                    {t("Book a Call")}
                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AIPage;
